import React from 'react';
import { Box, Stack } from '@mui/material';
import MySkeleton from '../components/Skeleton';

/** Label + input placeholder, sized like the booking form's TextField/Select rows */
const FieldLoader = ({ inputHeight = 40 }: { inputHeight?: number }) => (
  <Box>
    <MySkeleton variant="rounded" width={120} height={14} sx={{ marginBottom: '8px' }} />
    <MySkeleton variant="rounded" width="100%" height={inputHeight} />
  </Box>
);

/**
 * Mirrors CallBookingCalendar's loaded layout by reusing its grid and card classes,
 * so the skeleton fills the dialog and lines up with the real form.
 */
const CallBookingPopupLoader = () => {
  return (
    <Box className="CallBookingCalendarGrid">
      {/* Left column: form */}
      <Box className="CallBookingCalendarCol">
        <Stack gap={2}>
          <Box>
            <MySkeleton variant="rounded" width={180} height={18} sx={{ marginBottom: '8px' }} />
            <MySkeleton variant="rounded" width="70%" height={14} />
          </Box>
          <FieldLoader />
          <FieldLoader />
          <FieldLoader />
          <FieldLoader />
          <FieldLoader inputHeight={100} />
          <Box>
            <MySkeleton variant="rounded" width={140} height={14} sx={{ marginBottom: '12px' }} />
            {[...Array(3).keys()].map((index) => (
              <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                <MySkeleton variant="circular" width={20} height={20} />
                <MySkeleton variant="rounded" width={160} height={14} />
              </Box>
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Right column: date picker + time slots */}
      <Box className="CallBookingCalendarCol CallBookingCalendarCol--right">
        <Box className="CallBookingCalendarRightGrid">
          <Box className="CallBookingCalendarCard">
            <Box display="flex" alignItems="center" justifyContent="center" gap="35px">
              <MySkeleton variant="circular" width={30} height={30} />
              <MySkeleton variant="rounded" width={120} height={20} />
              <MySkeleton variant="circular" width={30} height={30} />
            </Box>
            <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" rowGap="8px" justifyItems="center">
              {[...Array(7).keys()].map((index) => (
                <MySkeleton key={`day-${index}`} variant="rounded" width="60%" height={12} sx={{ maxWidth: 24 }} />
              ))}
              {[...Array(35).keys()].map((index) => (
                <Box key={`date-${index}`} width="100%" maxWidth={32} sx={{ aspectRatio: '1' }}>
                  <MySkeleton variant="circular" width="100%" height="100%" />
                </Box>
              ))}
            </Box>
          </Box>

          <Box className="CallBookingTimeSlotsCard">
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} mb={1}>
              <MySkeleton variant="rounded" width={120} height={20} />
              <MySkeleton variant="rounded" width={150} height={12} />
            </Box>
            <Box display="flex" flexDirection="column" gap="5px">
              {[...Array(7).keys()].map((index) => (
                <MySkeleton key={index} variant="rounded" width="100%" height={30} />
              ))}
            </Box>
          </Box>
        </Box>

        <Box>
          <MySkeleton variant="rounded" width={160} height={18} sx={{ marginBottom: '8px' }} />
          <MySkeleton variant="rounded" width="80%" height={14} />
        </Box>
      </Box>
    </Box>
  );
};

export default CallBookingPopupLoader;
